import heic2any from 'heic2any';
import { toast } from 'react-toastify';
import { Asset } from '@core/generated/gql-types';

export const getFileExtension = (key?: string | null) => {
  if (!key) return null;
  return key.split('.').pop()?.toLowerCase();
};

export const isDocumentPdf = (
  key?: string | null,
  contentType?: string | null,
) => {
  if (!key) return false;

  const ext = getFileExtension(key);

  return (
    ['pdf'].includes(ext ?? '') || contentType?.includes('application/pdf')
  );
};

export const isDocumentHeic = (key?: string | null) => {
  if (!key) return false;
  const ext = getFileExtension(key);
  return ['heif', 'heic'].includes(ext ?? '');
};

export const isImage = (key?: string | null) => {
  if (!key) return false;
  const ext = getFileExtension(key);
  return ['png', 'jpg', 'jpeg', 'gif'].includes(ext ?? '');
};

/**
 * Previews a file from a URL (e.g. S3 presigned URL) by fetching as blob
 * and passes the blob to the appropriate preview component.
 *
 * Handles expired/invalid links with user feedback.
 */
export const fetchBlobUrlForPreview = async (asset: Asset): Promise<string> => {
  try {
    if (!asset.url || !asset.key) {
      throw new Error('Asset URL and key are required');
    }

    const response = await fetch(asset.url);

    if (!response.ok) {
      const text = await response.text();
      const isExpired =
        text.includes('Request has expired') ||
        text.includes('AccessDenied') ||
        text.includes('<Code>AccessDenied</Code>');
      throw new Error(isExpired ? 'Preview link expired' : 'Preview failed');
    }

    const contentType = (
      response.headers.get('Content-Type') || ''
    ).toLowerCase();

    if (
      contentType.includes('application/xml') ||
      contentType.includes('text/xml') ||
      contentType.includes('text/html')
    ) {
      throw new Error('Preview link expired');
    }

    const blob = await response.blob();

    const isImageAsset = isImage(asset.key);
    const isPdf = isDocumentPdf(asset.key, contentType);
    const isHeic = isDocumentHeic(asset.key);

    if (isImageAsset) {
      return window.URL.createObjectURL(blob);
    }

    if (isPdf) {
      const typedBlob = new Blob([blob], {
        type: 'application/pdf',
      });
      return window.URL.createObjectURL(typedBlob);
    }

    if (isHeic) {
      const convertedBlob = await heic2any({
        blob,
        toType: 'image/jpeg',
      });
      const jpegBlob = Array.isArray(convertedBlob)
        ? convertedBlob[0]
        : convertedBlob;
      if (!jpegBlob) {
        throw new Error('Failed to convert HEIC to JPEG');
      }
      return window.URL.createObjectURL(jpegBlob);
    }

    return window.URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error previewing file:', error);
    toast.error('Failed to preview file. Please try again.', {
      position: 'bottom-left',
      autoClose: 3000,
    });
    return '';
  }
};
